/**
 * Chat (board #232) -- a tab per configured gateway, plus internal chat rooms.
 *
 * Be clear about the ceiling, because the board item asks for something the
 * backend cannot yet deliver:
 *
 * `WebhookService.process_webhook` had no URL, no view and no call site
 * anywhere in the repo, so no inbound message and no delivery receipt could
 * ever arrive. A receiver is now mounted (see
 * `apps/communications/webhooks.py`), which fixes delivery receipts -- but the
 * handlers behind it record delivery STATUS (delivered / bounced / opened).
 * Nothing anywhere creates a `Communication` with `direction='inbound'`, so a
 * customer's WhatsApp reply still has nowhere to land.
 *
 * What that means for this screen: the WhatsApp and Email tabs show the real
 * outbound messages that exist, grouped by counterparty, and say plainly that
 * replies cannot arrive yet. They do not invent conversations.
 *
 * The EMAIL tab now has a composer (#114), because since #259 there is an SMTP
 * adapter behind it and a mail can genuinely leave the building. The WhatsApp
 * tab still has none: nothing would carry the message, and a composer that
 * posts into a void is worse than no composer. What the email composer shows
 * afterwards is the DELIVERY RECORD the server returned -- its status and, on
 * a failure, the provider's own words. It never shows a green tick for a 201.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

import { Badge, Button, Card, EmptyState, Input, Modal, ModalFooter } from '@/components/common';
import { PageHeader } from '@/components/layout';
import { getErrorMessage } from '@/services';
import { cn, formatDateTime } from '@/utils';
import type { MessageThread } from '@/services/communications';
import {
  useChatRooms,
  useGateways,
  useMessageThreads,
  useRoomMessages,
  useSendEmail,
  useSendRoomMessage,
} from './useCommunicationGateways';

const INTERNAL_TAB = 'internal';

export function ChatPage() {
  const { data: gateways, isLoading: gatewaysLoading } = useGateways();

  // One tab per channel type that actually has a gateway row. No gateway
  // means no conversation could exist on that channel, so an empty tab for it
  // would be furniture.
  const gatewayTabs = useMemo(() => {
    const seen = new Map<string, string>();
    (gateways ?? [])
      .filter((gateway) => gateway.channel_type !== 'in_app' && gateway.channel_type !== 'push')
      .forEach((gateway) => {
        if (!seen.has(gateway.channel_type)) seen.set(gateway.channel_type, gateway.channel_type_display);
      });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [gateways]);

  const tabs = [...gatewayTabs, { value: INTERNAL_TAB, label: 'Internal' }];
  const [activeTab, setActiveTab] = useState<string>(INTERNAL_TAB);

  // If gateways load after the first render, land on the first gateway tab.
  const resolvedTab = tabs.some((tab) => tab.value === activeTab) ? activeTab : INTERNAL_TAB;

  return (
    <div>
      <PageHeader
        title="Chat"
        subtitle="Conversations by channel"
        breadcrumbs={[{ label: 'Communications', href: '/communications' }, { label: 'Chat' }]}
      />

      {!gatewaysLoading && gatewayTabs.length === 0 && (
        <Card className="mb-6 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900 dark:text-amber-200">
              No communication gateway is configured, so there are no WhatsApp or Email tabs. Add one
              under <span className="font-medium">Settings &rsaquo; Communication Gateways</span>.
            </p>
          </div>
        </Card>
      )}

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Channels">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={resolvedTab === tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium',
                resolvedTab === tab.value
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {resolvedTab === INTERNAL_TAB ? <InternalChatTab /> : <ChannelThreadsTab channelType={resolvedTab} />}
    </div>
  );
}

function ChannelThreadsTab({ channelType }: { channelType: string }) {
  const { data, isLoading } = useMessageThreads(channelType);
  const [searchParams] = useSearchParams();
  const [composeOpen, setComposeOpen] = useState(false);

  // Deep link from elsewhere in the app: /communications/chat?compose=email&to=...
  useEffect(() => {
    if (channelType === 'email' && searchParams.get('compose') === 'email') setComposeOpen(true);
  }, [channelType, searchParams]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, index) => (
          <Card key={index} className="animate-pulse">
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
          </Card>
        ))}
      </div>
    );
  }

  const threads = data?.threads ?? [];

  return (
    <div className="space-y-4">
      {channelType === 'email' && (
        <div className="flex justify-end">
          <Button leftIcon={<EnvelopeIcon className="w-4 h-4" />} onClick={() => setComposeOpen(true)}>
            Compose email
          </Button>
        </div>
      )}

      {composeOpen && (
        <ComposeEmailModal
          initialRecipient={searchParams.get('to') ?? ''}
          initialCustomerId={searchParams.get('customer') ?? ''}
          onClose={() => setComposeOpen(false)}
        />
      )}

      {/* The honest ceiling, stated on the tab rather than left to be
          discovered. `inbound_supported` comes from the server. */}
      {data && !data.inbound_supported && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <p className="text-sm text-blue-900 dark:text-blue-200">
            <span className="font-medium">Replies cannot arrive yet.</span> {data.inbound_note}
          </p>
        </Card>
      )}

      {data?.truncated && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Showing the {threads.length} most recent of {data.total_threads} conversations.
        </p>
      )}

      {threads.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ChatBubbleLeftRightIcon />}
            title="No conversations on this channel"
            description={
              'Nothing has been sent on this channel, and nothing can be received on it yet. ' +
              'This list shows real messages only — it stays empty until one exists.'
            }
          />
        </Card>
      ) : (
        threads.map((thread) => <ThreadRow key={thread.recipient} thread={thread} />)
      )}
    </div>
  );
}

/**
 * Compose one email (#114).
 *
 * The result panel is the point of this component. `sendEmail` resolves 201
 * for a message that FAILED to leave -- the server records the attempt and
 * puts the reason in `error_message` -- so the panel renders the returned
 * record's own status and reason. A toast saying "Sent" on a 201 would be
 * exactly the defect #259 was opened to close.
 */
function ComposeEmailModal({
  initialRecipient,
  initialCustomerId,
  onClose,
}: {
  initialRecipient: string;
  initialCustomerId: string;
  onClose: () => void;
}) {
  const [recipient, setRecipient] = useState(initialRecipient);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const sendMutation = useSendEmail();
  const record = sendMutation.data;
  const delivered = record ? record.status === 'sent' || record.status === 'delivered' : false;

  const canSend = (!!recipient.trim() || !!initialCustomerId) && !!subject.trim() && !!content.trim();

  return (
    <Modal isOpen onClose={onClose} title="Compose email" size="lg">
      <div className="space-y-4">
        <Input
          label="To"
          name="compose-recipient"
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="customer@example.com"
          helperText={initialCustomerId ? 'Leave blank to use the selected customer’s address.' : undefined}
        />
        <Input
          label="Subject"
          name="compose-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <div>
          <label htmlFor="compose-body" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Message
          </label>
          <textarea
            id="compose-body"
            name="compose-body"
            rows={8}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        {sendMutation.isError && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-900 dark:text-red-200" role="alert">
            {getErrorMessage(sendMutation.error)}
          </div>
        )}

        {record && (
          <div
            className={cn(
              'rounded-lg border p-3 text-sm',
              delivered ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-200' : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200'
            )}
            role="status"
          >
            <p className="font-medium">
              {delivered ? 'The message was accepted by the mail server.' : 'Nothing was sent.'}
            </p>
            <p className="mt-1">
              Status: <span className="font-mono">{record.status}</span>
            </p>
            {record.message_id && (
              <p className="mt-1 font-mono text-xs break-all">{record.message_id}</p>
            )}
            {record.error_message && <p className="mt-1">{record.error_message}</p>}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={!canSend}
          isLoading={sendMutation.isPending}
          onClick={() =>
            sendMutation.mutate({
              recipient: recipient.trim() || undefined,
              customer_id: initialCustomerId || undefined,
              subject: subject.trim(),
              content,
            })
          }
        >
          Send email
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ThreadRow({ thread }: { thread: MessageThread }) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {thread.recipient_name || thread.recipient}
            </h3>
            {thread.recipient_name && (
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{thread.recipient}</span>
            )}
            {thread.failed_count > 0 && (
              <Badge variant="error" size="sm">
                {thread.failed_count} failed
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{thread.last_preview}</p>
          {thread.last_error && <p className="mt-1 text-xs text-red-700 dark:text-red-400">{thread.last_error}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatDateTime(thread.last_message_at)}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {thread.outbound_count} sent
            {thread.inbound_count > 0 && ` · ${thread.inbound_count} received`}
          </p>
        </div>
      </div>
    </Card>
  );
}

function InternalChatTab() {
  const { data: rooms, isLoading } = useChatRooms(true);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const roomId = activeRoomId ?? rooms?.[0]?.id ?? null;

  const { data: messages } = useRoomMessages(roomId);
  const sendMutation = useSendRoomMessage();
  const [draft, setDraft] = useState('');

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
      </Card>
    );
  }

  if (!rooms || rooms.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<ChatBubbleLeftRightIcon />}
          title="No chat rooms"
          description="Internal chat rooms are created from a policy, claim or team discussion. You are not a participant in any yet."
        />
      </Card>
    );
  }

  const handleSend = () => {
    if (!roomId || !draft.trim()) return;
    sendMutation.mutate(
      { roomId, content: draft.trim() },
      { onSuccess: () => setDraft('') }
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1">
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {rooms.map((room) => (
            <li key={room.id}>
              <button
                type="button"
                onClick={() => setActiveRoomId(room.id)}
                className={cn(
                  'w-full text-left py-3 px-2 rounded-lg',
                  room.id === roomId ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                )}
              >
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{room.name}</p>
                {room.last_message_preview && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {room.last_message_preview.sender}: {room.last_message_preview.content}
                  </p>
                )}
              </button>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="lg:col-span-2 flex flex-col">
        <div className="flex-1 space-y-3 max-h-96 overflow-y-auto">
          {(messages ?? []).length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">No messages in this room yet.</p>
          ) : (
            (messages ?? []).map((message) => (
              <div
                key={message.id}
                className={cn('max-w-[80%] rounded-lg px-3 py-2', message.is_own ? 'ml-auto bg-primary-50 dark:bg-primary-900/20' : 'bg-gray-50 dark:bg-gray-700/50')}
              >
                <p className="text-xs text-gray-500 dark:text-gray-400">{message.sender_name || 'System'}</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{message.content}</p>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 flex gap-2 items-end">
          <div className="flex-1">
            <Input
              aria-label="Message"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Write a message..."
            />
          </div>
          <Button
            onClick={handleSend}
            disabled={!draft.trim() || !roomId}
            isLoading={sendMutation.isPending}
            leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
          >
            Send
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default ChatPage;
